import { useCallback, useState } from "react";

export interface UseSlotSelectionResult {
  selected: Set<string>;
  toggle: (slot: string) => void;
  toggleAll: (allSlots: string[]) => void;
  selectSlots: (slots: string[]) => void;
  clear: () => void;
}

export function useSlotSelection(initialSlots?: string[]): UseSlotSelectionResult {
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(initialSlots ?? [])
  );

  const toggle = useCallback((slot: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slot)) next.delete(slot);
      else next.add(slot);
      return next;
    });
  }, []);

  const toggleAll = useCallback((slots: string[]) => {
    setSelected((prev) => {
      const allSelected = slots.length > 0 && slots.every((s) => prev.has(s));
      const next = new Set(prev);
      if (allSelected) {
        slots.forEach((s) => next.delete(s));
      } else {
        slots.forEach((s) => next.add(s));
      }
      return next;
    });
  }, []);

  const selectSlots = useCallback((slots: string[]) => {
    setSelected((prev) => {
      const next = new Set(prev);
      slots.forEach((s) => next.add(s));
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelected(new Set()), []);

  return { selected, toggle, toggleAll, selectSlots, clear };
}
